import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { 
  Home, 
  FileText, 
  Users, 
  Calendar, 
  Settings, 
  LogOut, 
  Menu, 
  X, 
  Target, 
  PlusCircle,
  DollarSign,
  BarChart2,
  Bell,
  User,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Moon,
  Sun,
  CreditCard,
  Banknote,
  QrCode,
  Upload
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const NGODashboardLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [ngoProfile, setNgoProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch NGO profile data
  useEffect(() => {
    const fetchNGOProfile = async () => {
      try {
        const token = localStorage.getItem('ngoToken');
        const ngoUser = JSON.parse(localStorage.getItem('ngoUser') || '{}');
        const userType = localStorage.getItem('userType');
        
        if (!token || !ngoUser || userType !== 'ngo') {
          toast.error('Please login as an NGO to access this dashboard');
          navigate('/ngo/login');
          return;
        }
        
        // Set the ngoProfile from localStorage first for immediate display
        setNgoProfile(ngoUser);
        setLoading(false);
        
        // Skip the API call for now since we already have the profile from localStorage
        // This prevents the 404 errors in the console
        return;
        
        // Commented out to prevent 404 errors
        /*
        const response = await axios.get('http://localhost:3011/api/ngos/profile', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setNgoProfile(response.data);
        */
        
        // Loading is already set to false above
        // No need to set ngoProfile again as it's already set from localStorage
      } catch (error) {
        console.error('Error fetching NGO profile:', error);
        toast.error('Failed to load NGO profile');
        setLoading(false);
      }
    };

    fetchNGOProfile();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('ngoToken');
    navigate('/ngo/login');
    toast.success('Logged out successfully');
  };

  const toggleSubmenu = (index) => {
    if (activeSubmenu === index) {
      setActiveSubmenu(null);
    } else {
      setActiveSubmenu(index);
    }
  };

  const menuItems = [
    {
      icon: Target,
      label: 'Projects & Campaigns',
      path: '/ngo/dashboard/projects',
      hasSubmenu: true,
      submenu: [
        { label: 'All Projects', path: '/ngo/dashboard/projects' },
        { label: 'Create New Project', path: '/ngo/dashboard/projects/create' },
        { label: 'Manage Projects', path: '/ngo/dashboard/projects/manage' }
      ]
    },
    {
      icon: Users,
      label: 'Volunteer Management',
      path: '/ngo/dashboard/volunteers',
      hasSubmenu: true,
      submenu: [
        { label: 'All Volunteers', path: '/ngo/dashboard/volunteers' },
        { label: 'Add New Volunteer', path: '/ngo/dashboard/volunteers/add' },
        { label: 'Manage Volunteers', path: '/ngo/dashboard/volunteers/manage' }
      ]
    },
    {
      icon: Banknote,
      label: 'Banking & Payments',
      path: '/ngo/dashboard/banking',
      hasSubmenu: true,
      submenu: [
        { label: 'Bank Details', path: '/ngo/dashboard/banking/bank-details' },
        { label: 'Payment Proofs', path: '/ngo/dashboard/banking/payment-proofs' }
      ]
    },
    {
      icon: Settings,
      label: 'Profile Settings',
      path: '/ngo/dashboard/profile',
      hasSubmenu: false
    }
  ];

  return (
    <div className={`flex h-screen bg-gray-50 ${darkMode ? 'dark' : ''}`}>
      {/* Sidebar for desktop */}
      <aside 
        className={`fixed inset-y-0 left-0 z-10 flex flex-col bg-white border-r border-gray-200 transition-all duration-300 ease-in-out dark:bg-gray-800 dark:border-gray-700 ${
          collapsed ? 'w-20' : 'w-64'
        } ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
      >
        {/* Logo and collapse button */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
          <Link to="/ngo/dashboard" className="flex items-center">
            {!collapsed && (
              <span className="ml-2 text-xl font-semibold text-gray-800 dark:text-white">NGO Portal</span>
            )}
            {collapsed && (
              <span className="text-xl font-semibold text-gray-800 dark:text-white">NGO</span>
            )}
          </Link>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 focus:outline-none"
          >
            {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        {/* NGO Profile Summary */}
        {!collapsed && (
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white">
                {ngoProfile ? ngoProfile.name.charAt(0) : 'N'}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {loading ? 'Loading...' : (ngoProfile ? ngoProfile.name : 'NGO Name')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {ngoProfile?.status === 'approved' ? 'Verified' : 'Pending Verification'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Menu */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {menuItems.map((item, index) => (
            <div key={index}>
              <Link
                to={item.hasSubmenu ? '#' : item.path}
                onClick={item.hasSubmenu ? () => toggleSubmenu(index) : undefined}
                className={`flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                  location.pathname === item.path || 
                  (item.hasSubmenu && item.submenu.some(subItem => location.pathname === subItem.path))
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-200'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                }`}
              >
                <item.icon className={`${collapsed ? 'mx-auto' : 'mr-3'}`} size={20} />
                {!collapsed && (
                  <>
                    <span className="flex-1">{item.label}</span>
                    {item.hasSubmenu && (
                      <ChevronDown
                        className={`transition-transform duration-200 ${
                          activeSubmenu === index ? 'transform rotate-180' : ''
                        }`}
                        size={16}
                      />
                    )}
                  </>
                )}
              </Link>
              
              {/* Submenu */}
              {!collapsed && item.hasSubmenu && activeSubmenu === index && (
                <div className="pl-10 mt-1 space-y-1">
                  {item.submenu.map((subItem, subIndex) => (
                    <Link
                      key={subIndex}
                      to={subItem.path}
                      className={`flex items-center px-2 py-1.5 text-sm font-medium rounded-md transition-colors duration-200 ${
                        location.pathname === subItem.path
                          ? 'bg-blue-50 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white'
                      }`}
                    >
                      {subItem.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Bottom actions */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            {darkMode ? (
              <>
                {!collapsed && <span className="mr-2">Light Mode</span>}
                <Sun size={20} />
              </>
            ) : (
              <>
                {!collapsed && <span className="mr-2">Dark Mode</span>}
                <Moon size={20} />
              </>
            )}
          </button>
          
          <button
            onClick={handleLogout}
            className="flex items-center justify-center w-full px-4 py-2 mt-2 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800"
          >
            {!collapsed && <span className="mr-2">Logout</span>}
            <LogOut size={20} />
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className={`flex flex-col flex-1 transition-all duration-300 ease-in-out ${
        collapsed ? 'md:ml-20' : 'md:ml-64'
      }`}>
        {/* Top navbar */}
        <header className="z-10 py-4 bg-white shadow-sm dark:bg-gray-800">
          <div className="flex items-center justify-between px-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-1 text-gray-500 rounded-md md:hidden hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 focus:outline-none"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Search bar */}
            <div className="flex-1 max-w-md ml-4 md:ml-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-full py-2 pl-10 pr-4 text-gray-700 bg-gray-100 border border-transparent rounded-md dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 focus:outline-none focus:bg-white focus:border-blue-500 dark:focus:border-blue-500"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                  </svg>
                </div>
              </div>
            </div>

            {/* Right side icons */}
            <div className="flex items-center space-x-4">
              <button className="p-1 text-gray-500 rounded-md hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 focus:outline-none">
                <Bell size={20} />
              </button>
              <button className="p-1 text-gray-500 rounded-md hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 focus:outline-none">
                <User size={20} />
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
          <div className="container px-4 py-6 mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile menu backdrop */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 z-0 bg-gray-600 bg-opacity-50 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default NGODashboardLayout;
